import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const VesselCrewTab = ({ vessel, onVesselUpdate, user }) => {
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'crew',
    position: '',
    emergency_contact: '',
    emergency_phone: '',
    medical_conditions: '',
    certifications: '',
    start_date: '',
    end_date: '',
    notes: ''
  });

  const crewRoles = [
    { value: 'captain', label: 'Captain' },
    { value: 'first_mate', label: 'First Mate' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'deckhand', label: 'Deckhand' },
    { value: 'chef', label: 'Chef' },
    { value: 'steward', label: 'Steward' },
    { value: 'crew', label: 'Crew Member' },
    { value: 'guest', label: 'Guest' }
  ];

  const positions = [
    { value: 'captain', label: 'Captain' },
    { value: 'first_officer', label: 'First Officer' },
    { value: 'chief_engineer', label: 'Chief Engineer' },
    { value: 'second_engineer', label: 'Second Engineer' },
    { value: 'bosun', label: 'Bosun' },
    { value: 'deckhand', label: 'Deckhand' },
    { value: 'chef', label: 'Chef' },
    { value: 'steward', label: 'Steward' },
    { value: 'purser', label: 'Purser' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    loadCrew();
  }, [vessel?.id]);

  const loadCrew = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/crew`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch crew');
      }
      
      const data = await response.json();
      setCrew(data.crew || []);
    } catch (error) {
      console.error('Error loading crew:', error);
      toast.error('Failed to load crew');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCrewMember = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/crew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add crew member');
      }

      toast.success('Crew member added successfully');
      setShowAddModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'crew',
        position: '',
        emergency_contact: '',
        emergency_phone: '',
        medical_conditions: '',
        certifications: '',
        start_date: '',
        end_date: '',
        notes: ''
      });
      loadCrew();
    } catch (error) {
      console.error('Error adding crew member:', error);
      toast.error(error.message || 'Failed to add crew member');
    }
  };

  const handleEditCrewMember = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/crew/${selectedCrewMember.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update crew member');
      }

      toast.success('Crew member updated successfully');
      setShowEditModal(false);
      setSelectedCrewMember(null);
      loadCrew();
    } catch (error) {
      console.error('Error updating crew member:', error);
      toast.error(error.message || 'Failed to update crew member');
    }
  };

  const handleDeleteCrewMember = async (crewMemberId) => {
    if (!window.confirm('Are you sure you want to delete this crew member?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/crew/${crewMemberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete crew member');
      }

      toast.success('Crew member deleted successfully');
      loadCrew();
    } catch (error) {
      console.error('Error deleting crew member:', error);
      toast.error(error.message || 'Failed to delete crew member');
    }
  };

  const openEditModal = (crewMember) => {
    setSelectedCrewMember(crewMember);
    setFormData({
      first_name: crewMember.first_name || '',
      last_name: crewMember.last_name || '',
      email: crewMember.email || '',
      phone: crewMember.phone || '',
      role: crewMember.role || 'crew',
      position: crewMember.position || '',
      emergency_contact: crewMember.emergency_contact || '',
      emergency_phone: crewMember.emergency_phone || '',
      medical_conditions: crewMember.medical_conditions || '',
      certifications: crewMember.certifications || '',
      start_date: crewMember.start_date || '',
      end_date: crewMember.end_date || '',
      notes: crewMember.notes || ''
    });
    setShowEditModal(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'captain': return 'bg-marine-100 text-marine-800';
      case 'first_mate': return 'bg-marine-50 text-marine-700';
      case 'engineer': return 'bg-marine-100 text-marine-800';
      case 'chef': return 'bg-marine-50 text-marine-700';
      case 'steward': return 'bg-marine-100 text-marine-800';
      case 'deckhand': return 'bg-marine-50 text-marine-700';
      case 'crew': return 'bg-marine-100 text-marine-800';
      case 'guest': return 'bg-marine-50 text-marine-700';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderForm = (onSubmit, title) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  {crewRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Position</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  <option value="">Select Position</option>
                  {positions.map(position => (
                    <option key={position.value} value={position.value}>{position.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
              <input
                type="text"
                name="emergency_contact"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Emergency Phone</label>
              <input
                type="tel"
                name="emergency_phone"
                value={formData.emergency_phone}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Medical Conditions</label>
              <textarea
                name="medical_conditions"
                value={formData.medical_conditions}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Certifications</label>
              <textarea
                name="certifications"
                value={formData.certifications}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedCrewMember(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                {selectedCrewMember ? 'Update' : 'Add'} Crew Member
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading crew...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Crew Management</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Crew Member
        </button>
      </div>

      {/* Crew List */}
      {crew.length === 0 ? (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No crew members</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding a crew member.</p>
          <div className="mt-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Crew Member
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {crew.map((member) => (
            <div key={member.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-gray-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {member.first_name} {member.last_name}
                    </h3>
                    <p className="text-sm text-gray-500">{member.position || member.role}</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                      {member.role.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEditModal(member)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCrewMember(member.id)}
                    className="p-1 text-gray-400 hover:text-marine-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {member.email && (
                  <div className="flex items-center text-sm text-gray-600">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    {member.email}
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    {member.phone}
                  </div>
                )}
                {member.start_date && (
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Started: {new Date(member.start_date).toLocaleDateString()}
                  </div>
                )}
                {member.emergency_contact && (
                  <div className="flex items-center text-sm text-gray-600">
                    <IdentificationIcon className="h-4 w-4 mr-2" />
                    Emergency: {member.emergency_contact}
                  </div>
                )}
              </div>

              {member.notes && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">{member.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && renderForm(handleAddCrewMember, 'Add Crew Member')}
      {showEditModal && renderForm(handleEditCrewMember, 'Edit Crew Member')}
    </div>
  );
};

export default VesselCrewTab;
